// Panel 4 — Líneas de producción (mockup "Líneas de producción"): grid de
// tarjetas con semáforo por borde izquierdo, % grande, líder y paro acumulado.
// Las líneas SIEMPRE miden vs meta del turno (capacidad × horas capturadas).

import type { MinutosParoPorTipo, PantallaDatos } from 'schemas';
import { ConectividadBadge } from '../../../components/ui/conectividad-badge';
import { Icon } from '../../../components/ui/icon';
import { Barra, colorPct, ETIQUETA, fmt, PanelVacio, TituloPanel } from '../shared';

/**
 * Texto secundario de la tarjeta (líder, paros, minutos por tipo). Se lee en
 * proyector a varios metros: escala con el ancho, de 15 px a 1280 hasta 22 px.
 */
const SECUNDARIO = 'text-[clamp(0.95rem,1.15vw,1.4rem)]';

export function PanelLineas({ datos }: { datos: PantallaDatos }) {
  const activas = datos.lineas.filter((l) => l.estado !== 'sin_actividad');
  const inactivas = datos.lineas.filter((l) => l.estado === 'sin_actividad');
  const cols = datos.lineas.length > 9 ? 4 : 3;
  // Leyenda de señal solo si alguna línea está conectada a máquina.
  const hayMaquinas = datos.lineas.some((l) => l.conectividad);
  // minutos_paro solo suma paros cerrados; los abiertos llegan aparte y se
  // cruzan por código para no decir «sin paros» con una línea detenida.
  const abiertosPorLinea = new Map<string, { n: number; minutos: number }>();
  for (const p of datos.paros_abiertos ?? []) {
    const a = abiertosPorLinea.get(p.linea_codigo) ?? { n: 0, minutos: 0 };
    abiertosPorLinea.set(p.linea_codigo, { n: a.n + 1, minutos: a.minutos + p.minutos_abierto });
  }

  return (
    <section className="flex h-full flex-col gap-5 px-[4vw] py-[3vh]">
      <div className="flex items-end justify-between gap-6">
        <TituloPanel
          titulo="Líneas de producción"
          subtitulo={`${activas.length} línea${activas.length === 1 ? '' : 's'} activa${activas.length === 1 ? '' : 's'} · producido vs meta del turno`}
        />
        <div
          className={`flex items-center gap-4 pb-1 ${ETIQUETA} tracking-[0.1em] text-fg-tertiary`}
        >
          <Leyenda color="var(--ok)" label="≥ 90%" />
          <Leyenda color="var(--warn)" label="70–89%" />
          <Leyenda color="var(--crit)" label="< 70%" />
          {hayMaquinas ? (
            <span className="flex items-center gap-1.5 border-l border-border pl-4">
              <Icon name="wifi" size={12} stroke={2} className="text-fg-tertiary" />
              señal de máquina
            </span>
          ) : null}
        </div>
      </div>
      {datos.resumen_hoy ? <FranjaPlanta resumen={datos.resumen_hoy} /> : null}
      {datos.lineas.length === 0 ? (
        <PanelVacio mensaje="Sin líneas configuradas en la planta" />
      ) : (
        <div
          className="grid min-h-0 flex-1 auto-rows-fr gap-[1.2vw] overflow-hidden"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {activas.map((l) => {
            // Paro abierto: la tarjeta entera se tiñe de rojo para verse desde lejos.
            const conParoAbierto = abiertosPorLinea.has(l.codigo);
            return (
              <div
                key={l.linea_id}
                data-linea-tarjeta=""
                data-paro-abierto={conParoAbierto ? 'true' : 'false'}
                className={`flex min-w-0 flex-col justify-between rounded-lg border p-[1vw] ${
                  conParoAbierto ? 'border-crit bg-crit-soft' : 'border-border bg-surface-elevated'
                }`}
                style={{ borderLeft: `3px solid ${colorPct(l.cumplimiento_pct)}` }}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className={`shrink-0 font-mono ${ETIQUETA} text-fg-tertiary`}>
                      {l.codigo}
                    </span>
                    <span className="truncate text-[clamp(0.95rem,1.2vw,1.35rem)] font-medium text-fg">
                      {l.nombre}
                    </span>
                    <ConectividadBadge
                      conectividad={l.conectividad}
                      ultimoLatidoAt={l.ultimo_latido_at}
                      className="self-center"
                    />
                  </span>
                  <span
                    className="shrink-0 text-[clamp(1.2rem,1.8vw,2.1rem)] font-medium leading-none"
                    style={{
                      color: colorPct(l.cumplimiento_pct),
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {l.cumplimiento_pct === null ? '—' : `${Math.round(l.cumplimiento_pct)}%`}
                  </span>
                </div>
                <div className="my-[1vh]">
                  <Barra pct={l.cumplimiento_pct} height={8} />
                </div>
                <div
                  className={`flex items-center justify-between gap-3 ${SECUNDARIO}`}
                  data-linea-secundario=""
                >
                  <span className="truncate text-fg-secondary">{l.lider_nombre ?? '—'}</span>
                  <TextoParo
                    minutosCerrados={l.minutos_paro}
                    abiertos={abiertosPorLinea.get(l.codigo)}
                  />
                </div>
                <ValorLinea
                  tipos={l.minutos_paro_por_tipo ?? null}
                  kg={l.sobrellenado_kg_hoy ?? null}
                  gUnidad={l.sobrellenado_g_unidad_hoy ?? null}
                />
              </div>
            );
          })}
          {inactivas.map((l) => (
            <div
              key={l.linea_id}
              className="flex min-w-0 flex-col justify-between rounded-lg border border-dashed border-border p-[1vw] opacity-60"
            >
              <div className="flex items-baseline gap-2">
                <span className={`shrink-0 font-mono ${ETIQUETA} text-fg-tertiary`}>
                  {l.codigo}
                </span>
                <span className="truncate text-[clamp(0.95rem,1.2vw,1.35rem)] text-fg-tertiary">
                  {l.nombre}
                </span>
                <ConectividadBadge
                  conectividad={l.conectividad}
                  ultimoLatidoAt={l.ultimo_latido_at}
                  className="self-center"
                />
              </div>
              <span className={`${ETIQUETA} tracking-[0.14em] text-fg-tertiary`}>
                SIN ACTIVIDAD
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function TextoParo({
  minutosCerrados,
  abiertos,
}: {
  minutosCerrados: number;
  abiertos: { n: number; minutos: number } | undefined;
}) {
  const total = minutosCerrados + (abiertos?.minutos ?? 0);
  const texto = abiertos
    ? `⚠ ${abiertos.n === 1 ? 'paro abierto' : `${abiertos.n} paros abiertos`} · ${fmt.format(total)} m`
    : total > 0
      ? `⚠ ${fmt.format(total)} m paro`
      : 'sin paros';
  return (
    <span
      className="shrink-0"
      style={{
        color: abiertos || total >= 60 ? 'var(--crit)' : 'var(--fg-tertiary)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {texto}
    </span>
  );
}

// ── B11: en qué se fue el turno y cuánto se regaló hoy ─────────────────────
// Solo aparece si el API manda los campos (plantas con líneas de máquina y
// categorías marcadas); en los demás tenants el panel queda idéntico.

const TIPOS: ReadonlyArray<{ clave: keyof MinutosParoPorTipo; label: string; color: string }> = [
  { clave: 'averia', label: 'Avería', color: 'var(--crit)' },
  { clave: 'espera', label: 'Espera', color: 'var(--warn)' },
  { clave: 'cambio', label: 'Cambio', color: 'var(--info)' },
  { clave: 'otros', label: 'Otros', color: 'var(--fg-tertiary)' },
  // Causal pendiente de ingesta (SC-MAQ): su categoría es Espera por diseño,
  // pero aquí se rotula igual que en el supervisor y la tablet.
  { clave: 'sin_clasificar', label: 'Sin clasificar', color: 'var(--fg-secondary)' },
];

const fmtKg = new Intl.NumberFormat('es-CO', {
  maximumFractionDigits: 1,
  signDisplay: 'exceptZero',
});

function tiposConMinutos(tipos: MinutosParoPorTipo) {
  return TIPOS.filter((t) => Math.round(tipos[t.clave] ?? 0) > 0);
}

/** Segmentos «Avería 32 m · Espera 8 m…» (oculta los tipos en cero). */
function Segmentos({ tipos }: { tipos: MinutosParoPorTipo }) {
  return (
    <>
      {tiposConMinutos(tipos).map((t) => (
        <span key={t.clave} className="flex shrink-0 items-center gap-1.5" data-tipo={t.clave}>
          <span
            className="h-2 w-2 shrink-0 rounded-sm"
            style={{ background: t.color }}
            aria-hidden="true"
          />
          <span className="text-fg-secondary">{t.label}</span>
          <span className="font-medium text-fg" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {fmt.format(tipos[t.clave] ?? 0)} m
          </span>
        </span>
      ))}
    </>
  );
}

/** Barra apilada de 4 px: proporción del tiempo perdido por tipo. */
function BarraTipos({ tipos }: { tipos: MinutosParoPorTipo }) {
  const presentes = tiposConMinutos(tipos);
  const total = presentes.reduce((s, t) => s + (tipos[t.clave] ?? 0), 0);
  if (total <= 0) return null;
  return (
    <div
      className="flex h-1 w-full overflow-hidden rounded-full bg-surface-overlay"
      aria-hidden="true"
    >
      {presentes.map((t) => (
        <span
          key={t.clave}
          className="h-full"
          style={{ width: `${((tipos[t.clave] ?? 0) / total) * 100}%`, background: t.color }}
        />
      ))}
    </div>
  );
}

function TextoRegalado({ kg, gUnidad }: { kg: number; gUnidad: number | null }) {
  return (
    <span className="flex shrink-0 items-baseline gap-1.5" data-regalado="">
      <span
        className="font-medium"
        style={{
          color: kg > 0 ? 'var(--accent)' : 'var(--fg-secondary)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {fmtKg.format(kg)} kg
      </span>
      <span className="text-fg-secondary">regalados hoy</span>
      {gUnidad !== null ? (
        <span className="text-fg-tertiary" style={{ fontVariantNumeric: 'tabular-nums' }}>
          ({fmtKg.format(gUnidad)} g/u)
        </span>
      ) : null}
    </span>
  );
}

function ValorLinea({
  tipos,
  kg,
  gUnidad,
}: {
  tipos: MinutosParoPorTipo | null;
  kg: number | null;
  gUnidad: number | null;
}) {
  const hayTipos = tipos !== null && tiposConMinutos(tipos).length > 0;
  if (!hayTipos && kg === null) return null;
  return (
    <div className="mt-[0.8vh] flex min-w-0 flex-col gap-1.5" data-valor-linea="">
      {hayTipos ? <BarraTipos tipos={tipos} /> : null}
      <div
        className={`flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 overflow-hidden ${ETIQUETA}`}
        data-valor-texto=""
      >
        {hayTipos ? <Segmentos tipos={tipos} /> : null}
        {kg !== null ? <TextoRegalado kg={kg} gUnidad={gUnidad} /> : null}
      </div>
    </div>
  );
}

/** Franja de la planta bajo el título: totales del turno y kilos del día. */
function FranjaPlanta({ resumen }: { resumen: NonNullable<PantallaDatos['resumen_hoy']> }) {
  const hayTipos = tiposConMinutos(resumen.minutos_paro_por_tipo).length > 0;
  if (!hayTipos && resumen.sobrellenado_kg === null) return null;
  return (
    <div
      className={`flex min-w-0 flex-wrap items-center gap-x-5 gap-y-1.5 overflow-hidden rounded-lg border border-border bg-surface-elevated px-[1.2vw] py-[1vh] ${ETIQUETA}`}
      data-franja-planta=""
    >
      <span className="shrink-0 uppercase tracking-[0.12em] text-fg-tertiary">
        En qué se fue el turno
      </span>
      {hayTipos ? (
        <Segmentos tipos={resumen.minutos_paro_por_tipo} />
      ) : (
        <span className="text-fg-secondary">sin paros</span>
      )}
      {resumen.sobrellenado_kg !== null ? (
        <span className="flex min-w-0 items-center gap-5 border-l border-border pl-5">
          <TextoRegalado kg={resumen.sobrellenado_kg} gUnidad={null} />
        </span>
      ) : null}
    </div>
  );
}

function Leyenda({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} aria-hidden="true" />
      {label}
    </span>
  );
}
